import React, { useMemo, useCallback } from 'react';
import { useAccount } from '@bnnvara/accounts';
import { GridItem } from '@bnnvara/grids';
import { BaseForm } from '@bnnvara/forms/src/new_components/BaseForm/BaseForm';
import { Checkbox } from '@bnnvara/forms/src/new_components/inputs/Checkbox/Checkbox';
import { useMutation, useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { track } from '@bnnvara/analytics/src/track';
import { ThemeTagCard } from '../../Tags/ThemeTagCard';
import * as S from './styled';
import FetchFollowedThemes from '../../../api/queries/me/themes.gql';
import toggleFollowTheme from '../../../api/mutations/themeFollow.gql';
import AllThemesQuery from '../../../api/queries/allThemes.gql';

function ProfileSubThemes({ parentId, followedThemeIds, handleToggleFollow }) {
  const { data } = useQuery(AllThemesQuery, {
    variables: {
      parentId,
    },
  });

  return (
    <S.ThemesList>
      {data?.themes?.edges?.map(({ node: item }) => (
        <S.ThemesListItem key={item.id}>
          <Checkbox
            name={item.id}
            label={item.name}
            value={followedThemeIds?.includes(item.id)}
            onChange={(event) => handleToggleFollow(event, item)}
          />
        </S.ThemesListItem>
      ))}
    </S.ThemesList>
  );
}

export function ProfileFollowThemes() {
  const {
    token,
    profile: { UID: userId },
  } = useAccount();
  const [toggleFollow] = useMutation(toggleFollowTheme);

  /** Fetch all avaiable themes */
  const { data: themesData } = useQuery(AllThemesQuery, {
    variables: { parentId: null },
  });

  const themes = themesData?.themes?.edges;

  /** Fetch all themes followed by the user */
  const { data: profileThemes } = useQuery(FetchFollowedThemes, {
    variables: {
      userId,
    },
    skip: !userId,
  });

  const followedThemeIds = useMemo(
    () => profileThemes?.me?.followedThemes?.map((item) => item.id),
    [profileThemes]
  );

  const handleToggleFollow = useCallback(
    (value, subtheme) => {
      const trackingLabel = `${value === false ? 'un' : ''}follow-${
        subtheme.name
      }`;
      track('themeFollow', 'click', trackingLabel);

      toggleFollow({
        context: {
          headers: {
            'x-jwt-token': token,
          },
        },
        variables: {
          themeId: subtheme.id.toString(),
          isFollowing: value,
        },
      });
    },
    [toggleFollow, token]
  );

  return (
    <>
      <GridItem columnStartSm="1" columnEndSm="12">
        <S.Title element="h2" variant="one">
          BNNVARA Thema&lsquo;s volgen
        </S.Title>
        <S.Subtitle>
          Selecteer hieronder waarover je op de hoogte wil blijven:
        </S.Subtitle>
        <BaseForm id="themes-form">
          <S.ThemesContainer>
            {themes?.map(({ node: theme }) => (
              <S.ThemesCategoryWrapper key={theme.id}>
                <ThemeTagCard
                  mb="24px"
                  display="block"
                  color={theme.color}
                  textTransform="upperCase"
                  variant="profile"
                >
                  {theme.name}
                </ThemeTagCard>
                <ProfileSubThemes
                  parentId={theme.id}
                  followedThemeIds={followedThemeIds}
                  handleToggleFollow={handleToggleFollow}
                />
              </S.ThemesCategoryWrapper>
            ))}
          </S.ThemesContainer>
        </BaseForm>
      </GridItem>
    </>
  );
}

ProfileSubThemes.propTypes = {
  parentId: PropTypes.string,
  followedThemeIds: PropTypes.arrayOf(PropTypes.string),
  handleToggleFollow: PropTypes.func,
};
